/**
 * ホイール UI（RadioCardGroup の breakOut モード）のスクロール入力を一元管理する
 * ステートマシン。
 *
 * wheel / touch / 慣性（momentum）のすべての入力 delta を `input()` に集約し、
 * 「リスト回転 → 中央ナッジ（settling）→ カード内スクロール（card）→ ため（detent）
 * → リスト回転」の一本のチェーンとして扱う。従来の「カード内に余地があれば即
 * ネイティブスクロールへパスする」即断方式（findCardScrollTarget / touchPassthrough）
 * が生んでいた、境界でのホイール回転とカード内スクロールの取り合いを根治する。
 *
 * 状態遷移:
 *
 *   list ──(入力が SNAP_IDLE_MS 途切れる / 慣性終了)──▶ settling
 *   settling ──(最寄りステップへ到達 = aligned)──▶ card   [settled=true]
 *   settling ──(新しいユーザー入力)──▶ list
 *   card ──(カード内スクロールの余地が尽きる / 余地なし)──▶ detent
 *   detent ──(新しいジェスチャで DETENT_RELEASE_PX or DETENT_RELEASE_MS 突破)──▶ list
 *   detent ──(逆方向入力でカード内に余地あり)──▶ card
 *   card / detent ──(外部要因で中央から外れた)──▶ list
 *
 * DOM には直接触れず、注入されたコールバック（scrollList / requestSnap /
 * getCardScrollEl）を通して副作用を起こす。時刻も `now` で注入可能なため、
 * vitest のフェイクタイマーで全遷移を検証できる。
 */

/** カード内スクロール対象の最小インターフェイス（HTMLElement が構造的に満たす）。 */
export interface CardScrollTarget {
  scrollTop: number;
  readonly scrollHeight: number;
  readonly clientHeight: number;
}

/**
 * スナップ依頼の結果。
 *  - "started": 最寄りステップへの smooth scroll を開始した（到達待ち）。
 *  - "aligned": 既にステップ上に整列している（即 settled にできる）。
 *  - "unavailable": スナップできない位置（円→扇モーフ区間など）。
 */
export type SnapRequestResult = "started" | "aligned" | "unavailable";

export type GestureState = "list" | "settling" | "card" | "detent";

/**
 * 入力の出どころ。
 *  - "wheel": ホイール / トラックパッド。ノイズ・慣性テールが混ざる。
 *  - "touch": タッチのドラッグ。1 イベントの移動量が小さくても明確な操作意思。
 *  - "momentum": 指を離した後の慣性。detent を絶対に押し破れない。
 */
export type InputSource = "wheel" | "touch" | "momentum";

/**
 * `input()` の処理結果。呼び出し側の慣性ループは "absorbed" を受け取ったら
 * 慣性を打ち切る（エネルギーが detent で死んだことを意味する）。
 */
export type InputResult = "list" | "card" | "absorbed";

export interface WheelGestureControllerOptions {
  /**
   * リスト（ホイール回転）を delta px ぶん進める。
   * 感度係数（GESTURE_SCROLL_GAIN 等）の適用は呼び出し側の責務。
   */
  scrollList: (deltaPx: number) => void;
  /** 最寄りステップへのスナップ（ナッジ）を依頼する。 */
  requestSnap: () => SnapRequestResult;
  /**
   * 中央（選択中）カードのカード内スクロール要素（`data-card-scroll`）。
   * 無ければ null。card / detent 状態の入力ルーティングに使う。
   */
  getCardScrollEl: () => CardScrollTarget | null;
  /** settled（中央ナッジ完了状態）の変化通知。 */
  onSettleChange?: (settled: boolean) => void;
  /**
   * カード内スクロールへの引き渡し（card への入力ルーティングと detent）を
   * 有効にするか。false のときは settled 検知のみ行い、settle 後の入力は
   * 即座にリスト回転へ戻す（従来挙動 + アイドルスナップ）。
   */
  handoff?: boolean;
  /** テスト用の時刻源(ms)。既定 Date.now。 */
  now?: () => number;
}

// ===== チューニング定数 =====

/** リスト回転の入力がこれだけ途切れたら最寄りカードへスナップする(ms)。 */
export const SNAP_IDLE_MS = 160;
/** スナップ（smooth scroll）の到達を待つ上限(ms)。超えたら再試行する。 */
export const SETTLE_TIMEOUT_MS = 1200;
/** スナップ再試行の上限回数。超えたら list に戻す（自己修復は次の入力に任せる）。 */
export const SETTLE_MAX_RETRIES = 2;
/** settling 中、この大きさ未満の入力はノイズとして無視する(px)。 */
export const SETTLE_CANCEL_MIN_DELTA_PX = 2;
/**
 * ユーザー入力の「ジェスチャ」区切り(ms)。前回入力からこれ以上空いたら
 * 新しいジェスチャとみなす。トラックパッドの慣性テール（イベント間隔 <50ms）は
 * 同一ジェスチャとして扱われ、detent を押し破れない。
 */
export const GESTURE_GAP_MS = 140;
/** detent（ため）を突破してリスト回転へ引き渡すのに必要な蓄積入力量(px)。 */
export const DETENT_RELEASE_PX = 120;
/**
 * タッチ入力用の detent 突破閾値(px)。指のドラッグは移動量がそのまま
 * 画面上の距離になるため、wheel と同じ 120px を要求すると「スワイプが
 * 突っかかる / 途中で止まる」体感になる。短いフリックでも意思が明確なので
 * 低めに設定する。
 */
export const DETENT_RELEASE_TOUCH_PX = 48;
/** detent を時間で突破する場合の押し続け時間(ms)。 */
export const DETENT_RELEASE_MS = 260;
/** 時間突破に最低限必要な蓄積入力量(px)。1px のジッタで突破しないため。 */
export const DETENT_MIN_ACCUM_PX = 8;
/**
 * detent の蓄積で 1 イベントがカウントできる上限(px)。
 * 一撃の大きなホイールノッチ / 強いフリックで一瞬で突破しないよう、
 * 複数イベント（= 押し続ける意思）を要求する。
 */
export const DETENT_EVENT_CAP_PX = 40;

// ===== 内部ヘルパ =====

/** el が dir 方向（+1=下 / -1=上）にスクロールできる残り量(px)。 */
const scrollRoom = (el: CardScrollTarget, dir: 1 | -1): number =>
  dir > 0
    ? Math.max(0, el.scrollHeight - el.clientHeight - el.scrollTop)
    : Math.max(0, el.scrollTop);

export class WheelGestureController {
  private state: GestureState = "list";

  private settled = false;

  private pointerHeld = false;

  private destroyed = false;

  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  private settleRetries = 0;

  private lastInputTime = Number.NEGATIVE_INFINITY;

  /** 次のユーザー入力を強制的に新ジェスチャ扱いにする（touchstart 起点）。 */
  private forceNewGesture = false;

  private detentDir: 1 | -1 = 1;

  /**
   * detent 進入時に進行中だったジェスチャを封じるフラグ。
   * true の間の入力は蓄積されず absorbed になる（「末端に着いたら少し止める」）。
   * 新しいジェスチャの最初の入力で解除される。
   */
  private detentBlocked = false;

  private detentAccumPx = 0;

  private detentFirstInputTime: number | null = null;

  constructor(private readonly opts: WheelGestureControllerOptions) {}

  // ── 公開 API ─────────────────────────────────────────

  getState(): GestureState {
    return this.state;
  }

  isSettled(): boolean {
    return this.settled;
  }

  /**
   * wheel / touchmove / 慣性フレームからの入力。delta は「入力単位の px」
   * （ゲイン適用前）。戻り値でどこへルーティングされたかを返す。
   */
  input(deltaPx: number, source: InputSource = "wheel"): InputResult {
    if (this.destroyed || deltaPx === 0 || Number.isNaN(deltaPx)) {
      return "absorbed";
    }
    const t = this.now();
    let firstOfGesture = false;
    if (source !== "momentum") {
      firstOfGesture =
        this.forceNewGesture || t - this.lastInputTime > GESTURE_GAP_MS;
      this.forceNewGesture = false;
    }
    this.lastInputTime = t;

    switch (this.state) {
      case "list":
        return this.inputList(deltaPx);
      case "settling":
        return this.inputSettling(deltaPx, source);
      case "card":
        return this.inputCard(deltaPx, source, firstOfGesture, t);
      case "detent":
        return this.inputDetent(deltaPx, source, firstOfGesture, t);
      default:
        return "absorbed";
    }
  }

  /**
   * スクローラの scroll イベントごとに、現在位置がステップに整列しているかを
   * 通知してもらう。settling の到達検知と、外部要因で中央から外れた場合の
   * 実態同期（card / detent → list）に使う。
   */
  notifyScroll(aligned: boolean): void {
    if (this.destroyed) return;
    if (this.state === "settling") {
      if (aligned) this.enterCard();
      return;
    }
    if ((this.state === "card" || this.state === "detent") && !aligned) {
      this.toList();
    }
  }

  /**
   * クリック / キーボード等、呼び出し側起点の smooth scroll ナビゲーションの
   * 開始宣言。到達（aligned）で card / settled になる。
   */
  beginNavigation(): void {
    if (this.destroyed) return;
    this.clearIdleTimer();
    this.setSettled(false);
    this.state = "settling";
    this.settleRetries = 0;
    this.armSettleTimer();
  }

  /**
   * 現在位置からの settle（ナッジ）を明示的に試みる。
   * マウント直後・items 入れ替え直後・慣性終了後（endMomentum）の
   * 「静止状態からの整列」に使う。
   */
  requestSettle(): void {
    if (this.destroyed || this.pointerHeld) return;
    if (this.state === "card" || this.state === "detent") return;
    const result = this.opts.requestSnap();
    if (result === "aligned") {
      this.enterCard();
      return;
    }
    if (result === "started") {
      this.state = "settling";
      this.settleRetries = 0;
      this.armSettleTimer();
      return;
    }
    // unavailable: モーフ区間など。list のまま次の機会を待つ。
    this.state = "list";
    this.clearSettleTimer();
  }

  /** タッチの指が触れている間 true。アイドルスナップを保留する。 */
  setPointerHeld(held: boolean): void {
    if (this.destroyed) return;
    this.pointerHeld = held;
    if (held) {
      // タッチ開始は常に新しいジェスチャの起点。
      this.forceNewGesture = true;
      this.clearIdleTimer();
    }
  }

  /** 慣性が終了した（またはそもそも発生しなかった）ときに呼ぶ。 */
  endMomentum(): void {
    if (this.destroyed) return;
    // settling 中もスナップを依頼し直す: タッチ開始で smooth スナップを
    // 打ち切ったまま操作せず指を離すと、中途半端な位置のまま settling で
    // 止まる（自己修復はタイムアウト待ちになる）ため。
    if (this.state === "list" || this.state === "settling") {
      this.requestSettle();
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.clearIdleTimer();
    this.clearSettleTimer();
  }

  // ── 内部: 状態別の入力処理 ─────────────────────────────

  private inputList(deltaPx: number): InputResult {
    this.opts.scrollList(deltaPx);
    if (this.pointerHeld) {
      // 指が触れている間はアイドルスナップしない（touchend 起点で settle する）。
      this.clearIdleTimer();
    } else {
      this.armIdleTimer();
    }
    return "list";
  }

  private inputSettling(deltaPx: number, source: InputSource): InputResult {
    // 慣性の残滓ではスナップを中断しない。
    if (source === "momentum") return "absorbed";
    // wheel はトラックパッド慣性のテールやノイズが混ざるため閾値を設ける。
    // touch のドラッグは 1 イベントの移動量が小さくても明確な操作意思なので
    // 即キャンセルする（置いた指の動き始めがスナップに負ける問題の回避）。
    if (source === "wheel" && Math.abs(deltaPx) < SETTLE_CANCEL_MIN_DELTA_PX) {
      return "absorbed";
    }
    // 新しい操作意思: スナップを中断してリスト回転を再開する。
    this.clearSettleTimer();
    this.state = "list";
    return this.inputList(deltaPx);
  }

  private inputCard(
    deltaPx: number,
    source: InputSource,
    firstOfGesture: boolean,
    t: number,
  ): InputResult {
    if (!this.opts.handoff) {
      // 引き渡し無効: settle 検知のみ。入力は即リスト回転へ。
      this.setSettled(false);
      this.state = "list";
      return this.inputList(deltaPx);
    }

    const el = this.opts.getCardScrollEl();
    // カード内スクロールが存在しない / そもそもスクロール不能なカードでは
    // 「ため」を挟まず即リスト回転へ戻す（handoff 無効時と同じ経路）。
    // ここで detent に入れると、特にタッチの短いフリックが DETENT_RELEASE_PX
    // に届かず丸ごと吸収され、「スワイプしてもカードが動かない」原因になる。
    if (!el || el.scrollHeight - el.clientHeight <= 1) {
      this.setSettled(false);
      this.state = "list";
      return this.inputList(deltaPx);
    }

    const dir: 1 | -1 = deltaPx > 0 ? 1 : -1;
    const room = scrollRoom(el, dir);

    if (room > 0.5) {
      el.scrollTop += deltaPx;
      const leftover = Math.abs(deltaPx) - room;
      if (leftover > 0.5) {
        // 進行中のジェスチャの途中で末端に到達した。残りは「ため」で吸収し、
        // 一呼吸置いた新しいジェスチャだけが突破できる。
        this.enterDetent(dir, source === "momentum" || !firstOfGesture);
      }
      return "card";
    }

    // スクロール可能だがこの方向には余地なし: detent へ。
    // この入力自体も detent 側で処理する。
    this.enterDetent(dir, source === "momentum" || !firstOfGesture);
    return this.inputDetent(deltaPx, source, firstOfGesture, t);
  }

  private inputDetent(
    deltaPx: number,
    source: InputSource,
    firstOfGesture: boolean,
    t: number,
  ): InputResult {
    const dir: 1 | -1 = deltaPx > 0 ? 1 : -1;

    if (dir !== this.detentDir) {
      // 逆方向: カード内に余地があればカード内スクロールへ戻る。
      const el = this.opts.getCardScrollEl();
      if (el && scrollRoom(el, dir) > 0.5) {
        this.state = "card";
        return this.inputCard(deltaPx, source, firstOfGesture, t);
      }
      // 余地が無ければ向きを変えて「ため」をやり直す。
      this.enterDetent(dir, source === "momentum");
    }

    // 慣性は detent を絶対に押し破れない。absorbed を返して慣性を打ち切らせる。
    if (source === "momentum") return "absorbed";

    if (firstOfGesture) this.detentBlocked = false;
    if (this.detentBlocked) return "absorbed";

    if (this.detentFirstInputTime === null) this.detentFirstInputTime = t;
    this.detentAccumPx += Math.min(Math.abs(deltaPx), DETENT_EVENT_CAP_PX);
    const heldMs = t - this.detentFirstInputTime;
    // タッチのドラッグは wheel より突破閾値を低くする（指の移動量が
    // そのまま距離になるため、120px はスワイプの大半を飲み込んでしまう）。
    const releasePx =
      source === "touch" ? DETENT_RELEASE_TOUCH_PX : DETENT_RELEASE_PX;
    const release =
      this.detentAccumPx >= releasePx ||
      (heldMs >= DETENT_RELEASE_MS && this.detentAccumPx >= DETENT_MIN_ACCUM_PX);
    if (!release) return "absorbed";

    // 「ため」を突破: リスト回転へ引き渡す（この delta から回転が始まる）。
    this.setSettled(false);
    this.state = "list";
    return this.inputList(deltaPx);
  }

  // ── 内部: 状態遷移 ────────────────────────────────────

  private enterCard(): void {
    this.state = "card";
    this.clearIdleTimer();
    this.clearSettleTimer();
    this.settleRetries = 0;
    this.setSettled(true);
  }

  private enterDetent(dir: 1 | -1, blocked: boolean): void {
    this.state = "detent";
    this.detentDir = dir;
    this.detentBlocked = blocked;
    this.detentAccumPx = 0;
    this.detentFirstInputTime = null;
  }

  private toList(): void {
    this.state = "list";
    this.clearSettleTimer();
    this.setSettled(false);
    // 外部要因で外れた場合も、入力が来なければ再整列させる。
    if (!this.pointerHeld) this.armIdleTimer();
  }

  private setSettled(next: boolean): void {
    if (this.settled === next) return;
    this.settled = next;
    this.opts.onSettleChange?.(next);
  }

  // ── 内部: タイマ ─────────────────────────────────────

  private now(): number {
    return this.opts.now ? this.opts.now() : Date.now();
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private armIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (this.destroyed || this.pointerHeld) return;
      if (this.state === "list") this.requestSettle();
    }, SNAP_IDLE_MS);
  }

  private clearSettleTimer(): void {
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
  }

  private armSettleTimer(): void {
    this.clearSettleTimer();
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      if (this.destroyed || this.state !== "settling") return;
      this.settleRetries += 1;
      if (this.settleRetries > SETTLE_MAX_RETRIES) {
        // 自己修復は次の入力（inputList → アイドルスナップ）に任せる。
        this.state = "list";
        return;
      }
      const result = this.opts.requestSnap();
      if (result === "aligned") {
        this.enterCard();
      } else if (result === "started") {
        this.armSettleTimer();
      } else {
        this.state = "list";
      }
    }, SETTLE_TIMEOUT_MS);
  }
}

export default WheelGestureController;
